import flatten from 'lowscore/flatten.js'
import sum from 'root-core/utils/array/sum.js'
import clone from 'lodash.clonedeep'
import sortCommissionPolicies from './sortCommissionPolicies.js'
import calculateProductCommission from './calculateProductCommission.js'
import roundMoney from '../roundMoney.js'
import {applicableSupplementSelector} from '../applicableSupplementReduction.js'
import jsonReviver from '@booking-services/json-reviver'

function commissionEngine ({
  priceBreakdown,
  commission,
  departure,
  features,
  useJsonReviver,
}){
  /**
   * useJsonReviver option is used by the external TCBP Tour MFE project
   * to transform a raw objects, into a compatible ones with LocalDate inside
   */
  if (useJsonReviver) {
    priceBreakdown = JSON.parse(JSON.stringify(priceBreakdown), jsonReviver)
    commission = JSON.parse(JSON.stringify(commission), jsonReviver)
    departure = JSON.parse(JSON.stringify(departure), jsonReviver)
  }
  if (!commission) {
    priceBreakdown.totalCommission = 0
    return priceBreakdown
  }

  priceBreakdown = clone(priceBreakdown)
  const sortedPolicies = sortCommissionPolicies({
    commissionPolicies: commission.policies,
    departureDate: departure.startDate,
  })
  let totalCommission =
    addExtraStayCommission({priceBreakdown,commission,sortedPolicies})
    + addAdditionalProductsCommission({priceBreakdown,commission,sortedPolicies})
    + addMiscellaneousProductsCommission({priceBreakdown,commission,sortedPolicies,departure})
    + addPassengerCommission({priceBreakdown,commission, sortedPolicies,departure,features})
    + addFlightsCommission(priceBreakdown, commission, sortedPolicies)

  priceBreakdown.totalCommission = roundMoney(totalCommission)
  return priceBreakdown
}

function addCommission ({
  commissionProduct,
  breakdownProduct,
  priceBreakdown,
  commission,
  sortedPolicies,
}) {
  const commissionAmount = calculateProductCommission({
    product: commissionProduct,
    priceBreakdown,
    commission,
    sortedPolicies,
  })
  breakdownProduct.commission = commissionAmount
  return commissionAmount
}

function addExtraStayCommission ({priceBreakdown,commission,sortedPolicies}) {
  const extraStays = priceBreakdown.extraStays
  return sum(['before', 'after'].map(key => {
    if (extraStays[key]) {
      const hotel = extraStays[key].selectedHotel
      const hotelProduct = extraStays[key]
      return addCommission({
        breakdownProduct: hotelProduct,
        commissionProduct: {
          productCode: hotel.code,
          typeCode: hotel.typeCode,
          brochureCode: hotel.brochureCode,
          totalPrice: extraStays[key].total,
        },
        priceBreakdown,
        commission,
        sortedPolicies,
      })
    }
  }).filter(a => a))
}

function addAdditionalProductsCommission ({priceBreakdown, commission, sortedPolicies}) {
  return sum(priceBreakdown.requestedAdditionalProducts.map(additionalProduct =>
    addCommission({
      breakdownProduct: additionalProduct,
      commissionProduct: {
        productCode: additionalProduct.productCode,
        totalPrice: additionalProduct.commisionableAmount || additionalProduct.amount,
        typeCode: additionalProduct.typeCode,
        category: additionalProduct.category,
        brochureCode: additionalProduct.brochureCode,
      },
      priceBreakdown,
      commission,
      sortedPolicies,
    })
  ))
}

function addMiscellaneousProductsCommission ({priceBreakdown, commission, sortedPolicies, departure}) {
  return sum(priceBreakdown.aggregatedMiscellaneousProducts
    .filter(p => p.attractsCommission)
    .map(miscellaneousProduct => {
      let commissionProduct = miscellaneousProduct.isSurcharge
        ? {
          surchargeTypeId: miscellaneousProduct.productCode,
          totalPrice: miscellaneousProduct.totalPrice,
          productCode: departure.productCode,
          category: departure.category,
          brochureCode: departure.brochureCode,
          typeCode: departure.typeCode,
          classificationId: departure.classificationId,
        }
        : {
          productCode: miscellaneousProduct.productCode,
          totalPrice: miscellaneousProduct.totalPrice,
          typeCode: miscellaneousProduct.typeCode,
          category: miscellaneousProduct.category,
          brochureCode: miscellaneousProduct.brochureCode,
        }
      return addCommission({
        breakdownProduct: miscellaneousProduct,
        commissionProduct,
        priceBreakdown,
        commission,
        sortedPolicies,
      })
    }))
}

function calculateOutputTax (roomPrice, operatingCompanyTax, features) {
  if (!features.useOperatingCompanyTax || !operatingCompanyTax || !operatingCompanyTax.rate) return 0
  return (roomPrice * operatingCompanyTax.rate * operatingCompanyTax.productPercentages)/((operatingCompanyTax.rate + 100) * 100)
}

function addPassengerCommission ({priceBreakdown, commission, sortedPolicies, departure, features}) {
  const rooms = priceBreakdown.rooms
  const passengers = priceBreakdown.passengers
  const appliedPromotions = priceBreakdown.discounts.promotions.applied
  const groupDiscount = priceBreakdown.discounts.groupDiscount
  const groupDiscountAmount = groupDiscount ? groupDiscount.amount : 0
  let totalCommission = 0

  const allPassengers = rooms.map(room => room.passengers.map(passenger => {
    passenger.roomType = room.roomType
    return passenger
  }))

  flatten(allPassengers).forEach((passenger, index) => {
    const outputTax = calculateOutputTax(passenger.basicRoomPrice, priceBreakdown.operatingCompanyTax, features)
    const {commissionReduction, discountReduction} = applicableSupplementSelector({
      supplementAdjustments: departure.prices.roomSupplementAdjustments,
      roomType: passenger.roomType,
    })
    const roomPrice = commissionReduction
      ? passenger.basicRoomPrice - passenger.applicableRoomTypeSupplement
      : passenger.basicRoomPrice
    const appliedSupplementsPromotions = passenger.supplementsPromotions && passenger.supplementsPromotions.filter(
      supplementPromo => supplementPromo.isFTD || appliedPromotions.find(appliedPromo => appliedPromo.code === supplementPromo.code))
    const supplementsPromotionsAmount = commissionReduction && appliedSupplementsPromotions
      ? appliedSupplementsPromotions.reduce((sum, promo) => promo.type === 'fixedAmount' || !discountReduction ? sum + promo.amount : sum, 0)
      : 0
    const frequentTravellerDiscount = (passenger.discounts.frequentTravellerDiscount && passenger.frequentTravellerDiscountAmount) || 0
    const discountPerPassenger = groupDiscountAmount / passengers.length
    const totalPrice = roomPrice - discountPerPassenger - frequentTravellerDiscount + supplementsPromotionsAmount - outputTax
    const passengerProduct = {
      productCode: departure.productCode,
      category: departure.category,
      brochureCode: departure.brochureCode,
      typeCode: departure.typeCode,
      classificationId: departure.classificationId,
      totalPrice,
      passengerIndex: index,
    }
    subtractPromotionsFromProductsEvenly(passengerProduct, appliedPromotions)
    totalCommission += addCommission({
      breakdownProduct: passenger,
      commissionProduct: passengerProduct,
      priceBreakdown,
      commission,
      sortedPolicies,
    })
    if (commissionReduction) {
      const supplementProduct = {
        productCode: departure.productCode,
        category: departure.category,
        brochureCode: departure.brochureCode,
        typeCode: departure.typeCode,
        classificationId: departure.classificationId,
        totalPrice: passenger.applicableRoomTypeSupplement,
        passengerIndex: index,
        isSupplementProduct: true,
      }

      totalCommission += addCommission({
        breakdownProduct: passenger,
        commissionProduct: supplementProduct,
        priceBreakdown,
        commission,
        sortedPolicies,
      })

    }
  })
  return roundMoney(totalCommission)
}

function addFlightsCommission (priceBreakdown, commission, sortedPolicies) {
  const appliedPromotions = priceBreakdown.discounts.promotions.applied
  const flights = priceBreakdown.flights
  let totalCommission = 0
  if (flights) {
    const flightsProduct = {
      productCode: 'AIRPROD',
      typeCode: 'NA',
      totalPrice: flights.breakdown.fare || flights.breakdown.calculatedBaseFareAmount,
    }
    subtractPromotionsFromProductsEvenly(flightsProduct, appliedPromotions)
    totalCommission += addCommission({
      breakdownProduct: flights,
      commissionProduct: flightsProduct,
      priceBreakdown,
      commission,
      sortedPolicies,
    })
  }
  return roundMoney(totalCommission)
}

function subtractPromotionsFromProductsEvenly (product, appliedPromotions) {
  appliedPromotions
    .filter(promotion =>
      promotion.products.find(p => p.productCode == product.productCode)
    )
    .forEach(promotion => {
      if (product.passengerIndex !== undefined) {
        product.totalPrice -= promotion.amountPerPassenger[product.passengerIndex]
          ? promotion.amountPerPassenger[product.passengerIndex].amount
          : 0
      } else {
        product.totalPrice -= promotion.amount
      }
    })

  delete product.passengerIndex
  return product
}

export default function (options) {
  return commissionEngine(options)
}
